import { IEventEmitter, IEventSource } from "../lib/eventing/events";

export type ArtifactDependency<TDep extends string = string, TProps extends {} = unknown> = {
    artifactId: string;
    type: TDep; // e.g. 'parent', 'source'
    properties: TProps;
}

export type Artifact<TArtifact extends string = string, TProps extends {} = unknown> = {
    id: string;
    type: TArtifact;
    correlationIDs: string[];
    properties: TProps;
    artifacts: Artifact[];
    dependencies: ArtifactDependency[];
}

export type ArtifactEvents = {
    artifactCreated: { artifact: Artifact };
    artifactUpdated: { artifact: Artifact, previous: Artifact };
    artifactDestroyed: { artifactId: string };
}

export type ArtifactEventEmitter = IEventEmitter<ArtifactEvents>;
export type ArtifactEventSource = IEventSource<ArtifactEvents>;

export class ArtifactStore {
    private artifacts: Map<string, Artifact> = new Map();
    private dependentsIndex: Map<string, Set<string>> = new Map(); // artifactId -> Set<dependentArtifactId>

    constructor(private readonly eventEmitter: ArtifactEventEmitter) {}

    public async get(id: string): Promise<Artifact | undefined> {
        return this.artifacts.get(id);
    }

    public async getAll(): Promise<Artifact[]> {
        return Array.from(this.artifacts.values());
    }

    public async getByDependency(artifactId: string): Promise<Artifact[]> {
        const dependents = this.dependentsIndex.get(artifactId);
        if (!dependents) return [];
        return Array.from(dependents).map(id => this.artifacts.get(id)).filter((a): a is Artifact => !!a);
    }

    public async getAllDescendants(artifactId: string): Promise<Artifact[]> {
        const descendants = new Set<string>();
        const queue = [artifactId];

        while (queue.length > 0) {
            const currentId = queue.shift()!;
            const dependents = this.dependentsIndex.get(currentId);
            if (dependents) {
                dependents.forEach(dependentId => {
                    if (!descendants.has(dependentId)) {
                        descendants.add(dependentId);
                        queue.push(dependentId);
                    }
                });
            }
        }

        return Array.from(descendants).map(id => this.artifacts.get(id)).filter((a): a is Artifact => !!a);
    }

    /**
     * Upserts an artifact or a tree of artifacts.
     * Returns the list of IDs that were upserted.
     */
    public async upsert(artifactOrTree: Artifact): Promise<string[]> {
        const flattened = this.flattenArtifacts([artifactOrTree]);
        const upsertedIds: string[] = [];
        
        for (const item of flattened) {
            upsertedIds.push(item.id);
            const existing = this.artifacts.get(item.id);
            
            // Update dependents index
            if (item.dependencies) {
                for (const dep of item.dependencies) {
                    if (!this.dependentsIndex.has(dep.artifactId)) {
                        this.dependentsIndex.set(dep.artifactId, new Set());
                    }
                    this.dependentsIndex.get(dep.artifactId)!.add(item.id);
                }
            }

            // In a real app, we might diff here to avoid spurious updates
            this.artifacts.set(item.id, item);
            
            if (existing) {
                this.eventEmitter.events.artifactUpdated.emit({ artifact: item, previous: existing });
            } else {
                this.eventEmitter.events.artifactCreated.emit({ artifact: item });
            }
        }
        return upsertedIds;
    }

    private flattenArtifacts(artifacts: Artifact[], parentId?: string): Artifact[] {
        const result: Artifact[] = [];
        
        for (const artifact of artifacts) {
            // Inject parent dependency if parentId is provided
            if (parentId) {
                if (!artifact.dependencies) artifact.dependencies = [];
                // Check if parent dependency already exists to avoid duplicates
                const hasParentDep = artifact.dependencies.some(d => d.artifactId === parentId && d.type === 'parent');
                if (!hasParentDep) {
                    artifact.dependencies.push({
                        artifactId: parentId,
                        type: 'parent',
                        properties: {}
                    });
                }
            }

            result.push(artifact);
            if (artifact.artifacts && artifact.artifacts.length > 0) {
                result.push(...this.flattenArtifacts(artifact.artifacts, artifact.id));
            }
        }
        return result;
    }

    public async destroy(artifactId: string) {
        // Cascade destroy to dependents
        const dependents = this.dependentsIndex.get(artifactId);
        if (dependents) {
            // Create a copy to iterate safely while modifying
            const dependentsList = Array.from(dependents);
            for (const dependentId of dependentsList) {
                await this.destroy(dependentId);
            }
        }

        // Remove from dependencies of others (cleanup index)
        const artifact = await this.artifacts.get(artifactId);
        if (artifact && artifact.dependencies) {
            for (const dep of artifact.dependencies) {
                const set = this.dependentsIndex.get(dep.artifactId);
                if (set) {
                    set.delete(artifactId);
                    if (set.size === 0) {
                        this.dependentsIndex.delete(dep.artifactId);
                    }
                }
            }
        }

        if (this.artifacts.has(artifactId)) {
            this.artifacts.delete(artifactId);
            this.eventEmitter.events.artifactDestroyed.emit({ artifactId });
        }
    }
}
